<script lang="ts">
  /// 担当者選択ドロップダウン (ADR 20260528 / PR-ACC-1)。
  /// 候補は invited + active。選択中 id が inactive / 一覧外でも「(無効)」付きで
  /// 表示を維持し、永続済み id が黙って消えないようにする。
  /// ※本 PR では UI 部品の提供のみ。各画面 (firm/lawyer/商談…) への配線は ACC-3 以降。
  import { onMount } from 'svelte';
  import { listAssignableAccounts } from '@shared/api/accounts';
  import type { AssignableAccount } from '@shared/types/account';

  interface Props {
    /// 選択中の account id、未選択は ''。
    value: string;
    /// 候補となる accounts。 未指定なら自前で listAssignableAccounts() する (親が一括ロードして
    /// 渡す場合は指定して N+1 fetch を避ける)。
    accounts?: AssignableAccount[] | null;
    optional?: boolean;
    placeholder?: string;
    disabled?: boolean;
    id?: string;
    /// 選択変更時に呼ばれる任意コールバック (inline 保存用)。新しい value を渡す。
    onChange?: (value: string) => void;
  }

  let {
    value = $bindable(''),
    accounts = null,
    optional = true,
    placeholder = '担当者を選択',
    disabled = false,
    id,
    onChange,
  }: Props = $props();

  let loaded = $state<AssignableAccount[]>([]);
  let loading = $state(false);
  let error = $state<string | null>(null);

  // 親から渡されたらそれを、 無ければ自前ロード分を候補母集団にする。
  let pool = $derived(accounts ?? loaded);

  // 候補: invited / active のみ。選択中が候補外なら detached として「(無効)」表示。
  let candidates = $derived(
    pool.filter((a) => a.status === 'invited' || a.status === 'active'),
  );
  let selectedDetached = $derived(
    value && !candidates.some((a) => a.id === value)
      ? (pool.find((a) => a.id === value) ?? null)
      : null,
  );

  onMount(async () => {
    if (accounts) return;
    loading = true;
    try {
      loaded = await listAssignableAccounts();
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      loading = false;
    }
  });

  function label(a: AssignableAccount): string {
    return a.display_name;
  }
</script>

<select
  bind:value
  {id}
  {disabled}
  onchange={() => onChange?.(value)}
>
  {#if optional}
    <option value="">{placeholder}</option>
  {/if}
  {#each candidates as a (a.id)}
    <option value={a.id}>{label(a)}</option>
  {/each}
  {#if selectedDetached}
    <option value={selectedDetached.id}>{label(selectedDetached)} (無効)</option>
  {/if}
</select>
{#if loading}
  <small class="hint">読込中…</small>
{/if}
{#if error}
  <small class="error">担当者の取得に失敗しました: {error}</small>
{/if}

<style>
  select {
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
    background: #fff;
  }
  .hint {
    color: #6b7280;
    font-size: 0.7rem;
  }
  .error {
    color: #b91c1c;
    font-size: 0.7rem;
  }
</style>
